import { apiLibrary } from "@/api";
import { ref } from "vue";
import { ValidateToken } from "@/composable/useValidateToken/constants";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import type { AxiosError } from "axios";
import type { AuthValidateTokenResponse } from "@/api/modules/auth/types";

export const useValidateToken = () => {
    const tokenIsValid = ref<boolean>(false);
    const tokenExpiresIn = ref<Date | undefined>();
    const tokenErrorMessage = ref<string | undefined>();
    const tokenIntervalChecker = ref<ReturnType<typeof setInterval>>();

    const authentication = useAuthenticationStore();
    const validate = async () => {
        try {
            const { data } = await apiLibrary.auth.validateToken();

            await onValidToken(data);
        } catch (err: unknown) {
            await onInvalidToken(err);
        }
    };

    const onValidToken = async (data: AuthValidateTokenResponse) => {
        tokenIsValid.value = data.tokenIsValid;
        tokenExpiresIn.value = new Date(data.tokenExpiresIn as string);
        authentication.setExpireDate(new Date(data.tokenExpiresIn as string));
        authentication.setTokenValidity(data.tokenIsValid);
    };

    const onInvalidToken = async (error: unknown) => {
        const err = error as AxiosError;
        tokenIsValid.value = false;
        tokenExpiresIn.value = new Date();
        authentication.setTokenValidity(false);
        authentication.setExpireDate(new Date());
        tokenErrorMessage.value = err.message;
        await authentication.logout();
    };

    const initializeTokenIntervalChecker = () => {
        tokenIntervalChecker.value = setInterval(async () => {
            await validate();
        }, ValidateToken.IntervalDuration);
    };

    const removeTokenIntervalChecker = () => {
        clearInterval(tokenIntervalChecker.value);
    };

    const initialize = async () => {
        await validate();
        initializeTokenIntervalChecker();
    };

    return {
        tokenIsValid,
        tokenExpiresIn,
        tokenErrorMessage,
        initialize,
        removeTokenIntervalChecker
    };
};
